// components/TravelInterests/TravelInterestsScreen.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Sparkles,
  Heart,
  Mountain,
  Globe,
  Coffee,
  Map,
  Palette,
  Music,
  Camera,
  ShoppingBag,
  Utensils,
  Castle,
  Cloud,
  Users,
  Star,
  TrendingUp,
  Compass,
  Calendar,
  Plane,
  Hotel,
  MapPin,
  Check,
  X,
  Loader,
  Search,
  Filter,
  ArrowLeft,
  Zap,
  Target,
  Sunrise,
  Moon,
  Tag,
  ChevronRight,
  Crown,
  Award,
  Navigation
} from 'lucide-react';
import { InterestsApi } from './InterestsApi';

function TravelInterests() {
  const navigate = useNavigate();
  const [allInterests, setAllInterests] = useState([]);
  const [userInterests, setUserInterests] = useState([]);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [activeHover, setActiveHover] = useState(null);
  
  const authToken = localStorage.getItem("token");

  // Enhanced Categories with gradients
  const categories = [
    { id: 'all', name: 'All Adventures', icon: Navigation, gradient: 'from-blue-500 to-purple-500', bg: 'from-blue-50 to-purple-50' },
    { id: 'adventure', name: 'Thrill & Adventure', icon: Mountain, gradient: 'from-orange-500 to-red-500', bg: 'from-orange-50 to-red-50' },
    { id: 'culture', name: 'Culture & History', icon: Globe, gradient: 'from-indigo-500 to-purple-500', bg: 'from-indigo-50 to-purple-50' },
    { id: 'food', name: 'Food & Drink', icon: Coffee, gradient: 'from-amber-500 to-orange-500', bg: 'from-amber-50 to-orange-50' },
    { id: 'nature', name: 'Nature & Wildlife', icon: Map, gradient: 'from-emerald-500 to-teal-500', bg: 'from-emerald-50 to-teal-50' },
    { id: 'arts', name: 'Arts & Creativity', icon: Palette, gradient: 'from-purple-500 to-pink-500', bg: 'from-purple-50 to-pink-50' },
    { id: 'entertainment', name: 'Entertainment', icon: Music, gradient: 'from-rose-500 to-red-500', bg: 'from-rose-50 to-red-50' },
    { id: 'wellness', name: 'Wellness & Relaxation', icon: Heart, gradient: 'from-green-500 to-emerald-500', bg: 'from-green-50 to-emerald-50' },
    { id: 'luxury', name: 'Luxury & Leisure', icon: Crown, gradient: 'from-yellow-500 to-amber-500', bg: 'from-yellow-50 to-amber-50' }
  ];

  // Enhanced Icon mapping
  const interestIcons = {
    'Adventure': Mountain,
    'Culture': Globe,
    'Food & Drink': Coffee,
    'Nature': Map,
    'Arts': Palette,
    'Music': Music,
    'Photography': Camera,
    'Shopping': ShoppingBag,
    'Fine Dining': Utensils,
    'History': Castle,
    'Relaxation': Cloud,
    'Wellness': Heart,
    'Sports': TrendingUp,
    'Nightlife': Users,
    'Festivals': Star,
    'Wildlife': MapPin,
    'Beaches': Cloud,
    'Mountains': Mountain,
    'Cities': Globe,
    'Countryside': Map,
    'Luxury': Crown,
    'Spa': Heart,
    'Yoga': Sunrise,
    'Hiking': Mountain,
    'Scuba': MapPin,
    'Museum': Castle,
    'Concerts': Music,
    'Markets': ShoppingBag,
    'Wine': Coffee,
    'Architecture': Castle,
    'Photography': Camera,
    'Camping': Moon,
    'Skiing': Mountain,
    'Surfing': MapPin
  };

  // Enhanced Color mapping with gradients
  const interestColors = {
    'Adventure': 'from-orange-500 via-red-500 to-pink-500',
    'Culture': 'from-blue-500 via-indigo-500 to-purple-500',
    'Food & Drink': 'from-amber-500 via-orange-500 to-red-500',
    'Nature': 'from-emerald-500 via-green-500 to-teal-500',
    'Arts': 'from-purple-500 via-pink-500 to-rose-500',
    'Music': 'from-rose-500 via-pink-500 to-red-500',
    'Photography': 'from-cyan-500 via-blue-500 to-indigo-500',
    'Shopping': 'from-pink-500 via-rose-500 to-red-500',
    'Fine Dining': 'from-amber-500 via-yellow-500 to-orange-500',
    'History': 'from-stone-500 via-gray-500 to-zinc-500',
    'Relaxation': 'from-sky-500 via-blue-500 to-cyan-500',
    'Wellness': 'from-green-500 via-emerald-500 to-teal-500',
    'Sports': 'from-red-500 via-orange-500 to-amber-500',
    'Nightlife': 'from-violet-500 via-purple-500 to-indigo-500',
    'Festivals': 'from-yellow-500 via-amber-500 to-orange-500',
    'Wildlife': 'from-brown-500 via-amber-500 to-orange-500',
    'Beaches': 'from-blue-500 via-cyan-500 to-teal-500',
    'Mountains': 'from-gray-500 via-stone-500 to-zinc-500',
    'Cities': 'from-indigo-500 via-purple-500 to-pink-500',
    'Countryside': 'from-green-500 via-emerald-500 to-teal-500',
    'Luxury': 'from-yellow-500 via-amber-500 to-orange-500',
    'Spa': 'from-teal-500 via-emerald-500 to-green-500',
    'Yoga': 'from-green-500 via-emerald-500 to-teal-500',
    'Hiking': 'from-orange-500 via-red-500 to-amber-500',
    'Scuba': 'from-blue-500 via-cyan-500 to-teal-500',
    'Museum': 'from-amber-500 via-orange-500 to-yellow-500',
    'Concerts': 'from-rose-500 via-pink-500 to-purple-500',
    'Markets': 'from-amber-500 via-yellow-500 to-orange-500',
    'Wine': 'from-purple-500 via-pink-500 to-rose-500',
    'Architecture': 'from-stone-500 via-gray-500 to-zinc-500',
    'Camping': 'from-emerald-500 via-green-500 to-teal-500',
    'Skiing': 'from-blue-500 via-cyan-500 to-sky-500',
    'Surfing': 'from-blue-500 via-teal-500 to-cyan-500'
  };

  // Default fallback icon
  const getIcon = (interestName) => {
    return interestIcons[interestName] || Compass;
  };

  // Default fallback color
  const getColor = (interestName) => {
    return interestColors[interestName] || 'from-blue-500 via-indigo-500 to-purple-500';
  };

  useEffect(() => {
    fetchInterestsData();
  }, []);

  const extractData = (res) =>
  res?.data?.response?.data ??
  res?.data?.data ??
  res?.data ??
  {};


const fetchInterestsData = async () => {
  setIsLoading(true);
  try {
    const allInterestsResponse = await InterestsApi.getAllTravelInterests(authToken);
    
    const interestsObject = 
      allInterestsResponse?.data || 
      allInterestsResponse?.response?.data || 
      allInterestsResponse || 
      {};
    
    const allInterestsData = [];
    Object.keys(interestsObject).forEach(key => {
      if (Array.isArray(interestsObject[key])) {
        allInterestsData.push(...interestsObject[key]);
      }
    });
    
    setAllInterests(allInterestsData);

    const userInterestsResponse = await InterestsApi.getUserTravelInterests(authToken);
    
    const userInterestsObject = 
      userInterestsResponse?.data || 
      userInterestsResponse?.response?.data || 
      userInterestsResponse || 
      {};
    
    let userInterestIds = [];
    Object.keys(userInterestsObject).forEach(key => {
      if (Array.isArray(userInterestsObject[key])) {
        userInterestsObject[key].forEach(interest => {
          if (interest && interest.id) {
            userInterestIds.push(interest.id);
          }
        });
      }
    });
    
    setUserInterests(userInterestIds);
    setSelectedInterests(userInterestIds);

  } catch (error) {
    console.error('Error fetching interests:', error);
    toast.error('Failed to load travel interests');
  } finally {
    setIsLoading(false);
  }
};

  const toggleInterest = (interestId) => {
    setSelectedInterests(prev => {
      if (prev.includes(interestId)) {
        return prev.filter(id => id !== interestId);
      } else {
        return [...prev, interestId];
      }
    });
  };

  const saveInterests = async () => {
    if (selectedInterests.length === 0) {
      toast.error('Please select at least one interest');
      return;
    }

    setIsSaving(true);
    try {
      await InterestsApi.updateUserTravelInterests(selectedInterests, authToken);
      
      setUserInterests(selectedInterests);
      
      // Enhanced success toast
      toast.success(
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full">
            <Sparkles size={20} className="text-white" />
          </div>
          <div>
            <div className="font-bold">Travel style saved!</div>
            <div className="text-sm opacity-90">Your preferences have been updated</div>
          </div>
        </div>
      );
      
      setTimeout(() => {
        navigate(-1);
      }, 1500);
      
    } catch (error) {
      console.error('Error saving interests:', error);
      toast.error(error.response?.data?.message || 'Failed to save interests');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredInterests = allInterests.filter(interest => {
    const matchesSearch = interest.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         interest.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || 
                           interest.category?.toLowerCase() === filterCategory;
    
    return matchesSearch && matchesCategory;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen  bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4  left-1/4 w-64 h-64 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-indigo-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="text-center relative z-10">
          <div className="relative">
            <div className="w-32 h-32 border-4 border-blue-500/10 border-t-blue-500 border-r-purple-500 rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Compass size={48} className="text-blue-400 animate-pulse-slow" />
            </div>
          </div>
          <p className="mt-8 text-2xl font-bold bg-gradient-to-r from-blue-200 to-purple-200 bg-clip-text text-transparent">
            Discovering your travel passions...
          </p>
          <p className="mt-3 text-blue-300/80">We're curating the perfect experiences for you</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-blue-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-r from-amber-200/20 to-pink-200/20 rounded-full blur-3xl"></div>
      </div>

      {/* Header with Glassmorphism */}
      <div className="relative">
        <div className="bg-gradient-to-br from-gray-900 via-blue-900  via-purple-800 via-pink-500 to-orange-500  text-white relative overflow-hidden">
          {/* Header background pattern */}
          <div className="absolute inset-0 ">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(120,119,198,0.15),transparent_50%)]"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(255,119,198,0.1),transparent_50%)]"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 relative z-10">
            <div className="flex items-center justify-between mb-12">
              <div className="flex items-center gap-6">
                <button
                  onClick={() => navigate(-1)}
                  className="p-4 hover:bg-white/10 rounded-2xl transition-all duration-300 backdrop-blur-sm border border-white/10 hover:border-white/20 group"
                >
                  <ArrowLeft size={24} className="group-hover:-translate-x-1 transition-transform" />
                </button>
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-3 bg-gradient-to-r from-blue-500 via-purple-500 via-pink-500 to-orange-500 rounded-2xl shadow-lg">
                      <Compass size={28} />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
                      Travel Passions
                    </h1>
                  </div>
                  <p className="text-blue-200/90 text-lg max-w-2xl">
                    Select what excites you about travel. We'll use this to personalize your journey.
                  </p>
                </div>
              </div>
              
              {/* Selection Counter */}
              <div className="hidden lg:block">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-3xl blur-xl"></div>
                  <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl">
                        <Target size={24} />
                      </div>
                      <div>
                        <div className="text-3xl font-bold">{selectedInterests.length}</div>
                        <div className="text-blue-200/80 text-sm">Interests selected</div>
                        <div className="text-xs text-blue-300/60 mt-1">Recommended: 5-8</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Search and Filter */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-2">
                <div className="relative group">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl blur opacity-30 group-hover:opacity-50 transition duration-500"></div>
                  <div className="relative">
                    <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-blue-300" size={22} />
                    <input
                      type="text"
                      placeholder="Search adventures, experiences, or passions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-16 pr-6 py-5 bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl text-white placeholder-blue-300/70 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-lg"
                    />
                  </div>
                </div>
              </div>
              
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-pink-500 rounded-3xl blur opacity-30 group-hover:opacity-50 transition duration-500"></div>
                <div className="relative">
                  <Filter className="absolute left-6 top-1/2 transform -translate-y-1/2 text-blue-300" size={22} />
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="w-full pl-16 pr-6 py-5 bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none text-lg"
                  >
                    {categories.map(category => (
                      <option key={category.id} value={category.id} className="bg-gray-900">
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <ChevronRight className="absolute right-6 top-1/2 transform -translate-y-1/2 text-blue-300 rotate-90" size={20} />
                </div>
              </div>
            </div>

            {/* Category Filters */}
            <div className="overflow-x-auto pb-4">
              <div className="flex gap-3 min-w-max">
                {categories.map(category => {
                  const Icon = category.icon;
                  const isActive = filterCategory === category.id;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setFilterCategory(category.id)}
                      className={`
                        flex items-center gap-3 px-5 py-3.5 rounded-2xl transition-all duration-300 whitespace-nowrap
                        ${isActive 
                          ? `bg-gradient-to-r ${category.gradient} text-white shadow-xl scale-105` 
                          : 'bg-white/10 text-blue-100 hover:bg-white/20 backdrop-blur-sm border border-white/10'
                        }
                      `}
                    >
                      <Icon size={20} />
                      <span className="font-medium">{category.name}</span>
                      {isActive && <Sparkles size={16} className="ml-1" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Floating Save Button - Top */}
        <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 z-20">
          <button
            onClick={saveInterests}
            disabled={isSaving || selectedInterests.length === 0}
            className={`
              px-8 py-4 rounded-2xl font-bold flex items-center gap-3 transition-all duration-300
              ${selectedInterests.length > 0
                ? 'bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow-2xl hover:shadow-blue-500/30 hover:scale-105'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }
              ${isSaving ? 'opacity-80' : ''}
            `}
          >
            {isSaving ? (
              <>
                <Loader size={22} className="animate-spin" />
                <span>Saving Your Journey...</span>
              </>
            ) : (
              <>
                <Sparkles size={22} />
                <span>Save {selectedInterests.length} Interests</span>
                <ChevronRight size={20} />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 pt-20">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Sidebar - Statistics & Info */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Selection Summary */}
              <div className="bg-gradient-to-br from-white to-blue-50/50 rounded-3xl shadow-2xl p-6 border border-blue-100/50 backdrop-blur-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl">
                    <Target size={20} className="text-white" />
                  </div>
                  Your Selection
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                      <span className="text-gray-600">Selected</span>
                    </div>
                    <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      {selectedInterests.length}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full"></div>
                      <span className="text-gray-600">Previously had</span>
                    </div>
                    <span className="text-xl font-bold text-gray-700">{userInterests.length}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full"></div>
                      <span className="text-gray-600">Available</span>
                    </div>
                    <span className="text-xl font-bold text-gray-700">{allInterests.length}</span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-6">
                  <div className="flex justify-between text-sm text-gray-500 mb-2">
                    <span>Selection progress</span>
                    <span>{Math.round((selectedInterests.length / 8) * 100)}%</span>
                  </div>
                  <div className="h-2 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min((selectedInterests.length / 8) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    {selectedInterests.length >= 8 ? 'Perfect! You can select more' : `${8 - selectedInterests.length} more to optimal selection`}
                  </div>
                </div>
              </div>

              {/* Quick Tips */}
              <div className="bg-gradient-to-br from-white to-amber-50/30 rounded-3xl shadow-xl p-6 border border-amber-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl">
                    <Zap size={20} className="text-white" />
                  </div>
                  Tips
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <div className="p-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mt-1">
                      <Check size={12} className="text-white" />
                    </div>
                    <span className="text-gray-600 text-sm">Select 5-8 interests for optimal recommendations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="p-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full mt-1">
                      <Star size={12} className="text-white" />
                    </div>
                    <span className="text-gray-600 text-sm">Mix adventure & relaxation for balanced trips</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="p-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mt-1">
                      <Award size={12} className="text-white" />
                    </div>
                    <span className="text-gray-600 text-sm">Choose interests that genuinely excite you</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            {/* Selected Interests Showcase */}
            {selectedInterests.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl">
                      <Sparkles size={24} className="text-white" />
                    </div>
                    Your Travel DNA
                  </h3>
                  <span className="px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 rounded-full font-semibold">
                    {selectedInterests.length} selected
                  </span>
                </div>
                
                <div className="bg-gradient-to-br from-white via-blue-50/50 to-purple-50/50 rounded-3xl p-6 border border-blue-100/50 shadow-xl">
                  <div className="flex flex-wrap gap-3">
                    {selectedInterests.map(interestId => {
                      const interest = allInterests.find(i => i.id === interestId);
                      if (!interest) return null;
                      const Icon = getIcon(interest.name);
                      const color = getColor(interest.name);
                      return (
                        <div
                          key={interestId}
                          onMouseEnter={() => setActiveHover(interestId)}
                          onMouseLeave={() => setActiveHover(null)}
                          className={`
                            inline-flex items-center gap-3 px-5 py-3 rounded-2xl transition-all duration-300
                            ${activeHover === interestId 
                              ? 'bg-white shadow-lg scale-105' 
                              : 'bg-white/80 backdrop-blur-sm shadow-md'
                            }
                          `}
                        >
                          <div className={`p-2.5 rounded-xl bg-gradient-to-r ${color} shadow-md`}>
                            <Icon size={18} className="text-white" />
                          </div>
                          <span className="font-semibold text-gray-900">{interest.name}</span>
                          <button
                            onClick={() => toggleInterest(interestId)}
                            className="ml-2 p-1.5 hover:bg-gray-100 rounded-full transition-all duration-200"
                          >
                            <X size={16} className="text-gray-500 hover:text-gray-700" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Interests Grid */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {filterCategory === 'all' ? 'Discover All Experiences' : `Explore ${categories.find(c => c.id === filterCategory)?.name}`}
              </h3>
              
              {filteredInterests.length === 0 ? (
                <div className="text-center py-16 bg-gradient-to-br from-white to-blue-50/30 rounded-3xl border border-blue-100">
                  <div className="w-24 h-24 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Compass size={48} className="text-blue-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-700 mb-3">No adventures found</h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    Try a different search term or browse all categories to discover amazing travel experiences.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredInterests.map(interest => {
                    const isSelected = selectedInterests.includes(interest.id);
                    const Icon = getIcon(interest.name);
                    const color = getColor(interest.name);
                    
                    return (
                      <div
                        key={interest.id}
                        onMouseEnter={() => setActiveHover(interest.id)}
                        onMouseLeave={() => setActiveHover(null)}
                        className={`
                          relative group overflow-hidden rounded-3xl transition-all duration-500
                          ${isSelected 
                            ? 'bg-gradient-to-br from-white to-blue-50/50 border-2 border-blue-500 shadow-2xl shadow-blue-200/30 scale-[1.02]' 
                            : 'bg-white border-2 border-gray-100 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-100/20'
                          }
                          ${activeHover === interest.id ? 'scale-[1.02]' : ''}
                        `}
                      >
                        {/* Background Glow */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                        
                        {/* Selection Badge */}
                        <div className="absolute top-4 right-4 z-10">
                          <div className={`
                            w-8 h-6 rounded-full flex items-center justify-center transition-all duration-300
                            ${isSelected 
                              ? 'bg-gradient-to-r from-blue-500 to-purple-500  shadow-lg shadow-blue-500/30' 
                              : 'bg-white/80 backdrop-blur-sm border-2 border-gray-200 shadow-sm'
                            }
                          `}>
                            {isSelected && <Check size={16} className="text-white" />}
                          </div>
                        </div>

                        {/* Icon Container */}
                        <div className="p-2">
                          <div className={`
                            p-5 rounded-2xl w-fit mb-5 transition-all duration-500 relative
                            ${isSelected 
                              ? `bg-gradient-to-r ${color} shadow-lg scale-110` 
                              : `bg-gradient-to-r ${color} opacity-90 group-hover:opacity-100 group-hover:scale-105`
                            }
                          `}>
                            <Icon size={28} className="text-white relative z-10" />
                            <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${color} blur-md opacity-30`}></div>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="px-6 pb-6">
                          <div className="space-y-4">
                            <div>
                              <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-gray-800 transition-colors">
                                {interest.name}
                              </h4>
                              {interest.description && (
                                <p className="text-gray-600/90 text-sm leading-relaxed line-clamp-2">
                                  {interest.description}
                                </p>
                              )}
                            </div>

                            {/* Tags */}
                            <div className="flex flex-wrap gap-2">
                              {interest.category && (
                                <span className="px-3 py-1.5 bg-gradient-to-r from-gray-50 to-white text-gray-700 text-xs font-semibold rounded-full border border-gray-200/80">
                                  {interest.category}
                                </span>
                              )}
                              {interest.popularity && (
                                <span className="px-3 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 text-xs font-semibold rounded-full border border-blue-200/50 flex items-center gap-1.5">
                                  <TrendingUp size={12} className="text-blue-500" />
                                  {interest.popularity}% popular
                                </span>
                              )}
                            </div>

                            {/* Toggle Button */}
                            <button
                              onClick={() => toggleInterest(interest.id)}
                              className={`
                                w-full py-3.5 rounded-xl font-semibold transition-all duration-300 mt-4
                                relative overflow-hidden
                                ${isSelected
                                  ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-300/50'
                                  : 'bg-gradient-to-r from-gray-50 to-white text-gray-700 border border-gray-200 hover:border-blue-300 hover:text-blue-700'
                                }
                              `}
                            >
                              {/* Button overlay */}
                              <div className={`
                                absolute inset-0 rounded-xl transition-all duration-300
                                ${isSelected 
                                  ? 'bg-gradient-to-r from-blue-500/5 to-indigo-500/5' 
                                  : 'bg-gradient-to-r from-blue-500/0 to-indigo-500/0 group-hover:from-blue-500/5 group-hover:to-indigo-500/5'
                                }
                              `}></div>
                              
                              <span className="relative flex items-center justify-center gap-2">
                                {isSelected ? (
                                  <>
                                    <Check size={10} className="text-green-500" />
                                    <span>Selected</span>
                                  </>
                                ) : (
                                  <>
                                    <span>+</span>
                                  </>
                                )}
                              </span>
                            </button>
                          </div>
                        </div>

                        {/* Bottom accent */}
                        <div className={`
                          absolute bottom-0 left-1/2 -translate-x-1/2 
                          h-1.5 w-20 rounded-full transition-all duration-500
                          ${isSelected 
                            ? `bg-gradient-to-r ${color}` 
                            : 'bg-gradient-to-r from-gray-200 to-gray-300'
                          }
                        `}></div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Save Section */}
      <div className="sticky bottom-0 left-0 right-0 bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 mt-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-white">
              <h3 className="text-2xl font-bold mb-2 flex items-center gap-3">
                <Sparkles size={24} className="text-yellow-300" />
                Ready to personalize your journey?
              </h3>
              <p className="text-blue-200/90">
                Your selected interests will shape unique travel recommendations just for you.
              </p>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="hidden md:block text-center">
                <div className="text-3xl font-bold text-white">{selectedInterests.length}</div>
                <div className="text-blue-200/80 text-sm">interests selected</div>
              </div>
              
              <button
                onClick={saveInterests}
                disabled={isSaving || selectedInterests.length === 0}
                className={`
                  px-10 py-5 rounded-2xl font-bold flex items-center justify-center gap-3 
                  transition-all duration-300 min-w-[220px]
                  ${selectedInterests.length > 0
                    ? 'bg-gradient-to-br from-gray-900 via-blue-900  via-purple-800 via-pink-500 to-orange-500 text-white shadow-2xl hover:shadow-blue-500/50 hover:scale-105'
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  }
                  ${isSaving ? 'opacity-80' : ''}
                `}
              >
                {isSaving ? (
                  <>
                    <Loader size={24} className="animate-spin" />
                    <span className="text-lg">Crafting Your Profile...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={24} />
                    <span className="text-lg">Save My Travel Style</span>
                    <ChevronRight size={20} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TravelInterests;